import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { CardWithHeader } from "@/components/card-with-header";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { VolumeApi } from "@/lib/volume-api";
import { getMessageFromError } from "@/lib/api-errors";

export const Route = createFileRoute(
  "/(app)/_main-layout/_in-store/in-store/agents",
)({
  component: AgentsListPage,
});

function AgentsListPage() {
  const context = Route.useRouteContext();

  const agentsQuery = useQuery({
    queryKey: ["agents", context.selectedApplicationId, context.accessToken],
    queryFn: () => {
      const api = new VolumeApi(
        context.accessToken,
        context.selectedApplicationId,
      );
      return api.getAgents();
    },
  });

  return (
    <div className="space-y-6">
      <CardWithHeader variant="exchange" label="In Store" title="Agents">
        {agentsQuery.isLoading ? (
          <div className="flex items-center justify-center py-10">
            <Spinner />
          </div>
        ) : agentsQuery.isError ? (
          <div className="py-6 text-sm text-red-600">
            Failed to load agents: {getMessageFromError(agentsQuery.error)}
          </div>
        ) : !agentsQuery.data || agentsQuery.data.length === 0 ? (
          <div className="py-10 text-center text-sm text-slate-500">
            No agents have been configured for this application yet.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Agent ID</TableHead>
                <TableHead>Agent Code</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Tag Serial Number</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {agentsQuery.data.map((agent) => (
                <TableRow
                  key={agent.id}
                  className="cursor-pointer hover:bg-slate-50"
                >
                  <TableCell className="font-medium">
                    <Link
                      to="/in-store/agents/$agentId/config"
                      params={{ agentId: agent.agentId }}
                      search={(s: { applicationId?: string }) => ({
                        applicationId: s?.applicationId,
                      })}
                      className="text-volume-text-purple hover:underline"
                    >
                      {agent.agentId}
                    </Link>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-slate-600">
                    {agent.agentCode}
                  </TableCell>
                  <TableCell className="text-slate-600">
                    {agent.location}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">
                    {agent.tagSerialNumber ?? "—"}
                  </TableCell>
                  <TableCell>
                    {agent.disabled ? (
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                        Disabled
                      </span>
                    ) : (
                      <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">
                        Enabled
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">
                    {new Date(agent.createdAt).toLocaleDateString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardWithHeader>
    </div>
  );
}
